import React, { useEffect } from "react";
import {
  ChevronDownIcon,
  CalendarRecurrentIcon,
  CalendarDayIcon,
  BlockIcon,
  InfoCircleIcon,
} from "./icons";
import { useWidth } from "../../../hooks/useWidth";

interface CalendarConfigDropdownProps {
  calendarEditMode: boolean;
  isSchedulingMode: boolean;
  isRescheduleMode: boolean;
  isTogglingEdit: boolean;
  configDropdownOpen: boolean;
  setConfigDropdownOpen: (v: boolean) => void;
  configDropdownRef: React.RefObject<HTMLDivElement>;
  runTour: boolean;
  onReplayTour: () => void;
  onScheduleSession: () => void;
  onStartRecurrent: () => void;
  onStartSpecificDay: () => void;
  onStartBlock: () => void;
  onOpenHelp?: () => void;
  onOpenRescheduleInfo?: () => void;
  onToday?: () => void;
  /** Oculta la sección "Ayuda" (guía, cómo funciona la agenda, horarios de reprogramación): la app mobile no la tiene. */
  hideHelp?: boolean;
}

export const CalendarConfigDropdown = ({
  calendarEditMode,
  isSchedulingMode,
  isRescheduleMode,
  isTogglingEdit,
  configDropdownOpen,
  setConfigDropdownOpen,
  configDropdownRef,
  runTour,
  onReplayTour,
  onScheduleSession,
  onStartRecurrent,
  onStartSpecificDay,
  onStartBlock,
  onOpenHelp,
  onOpenRescheduleInfo,
  hideHelp = false,
}: CalendarConfigDropdownProps) => {
  // Close dropdown on click outside (skip during tour)
  useEffect(() => {
    if (!configDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (runTour) return;
      if (configDropdownRef.current && !configDropdownRef.current.contains(e.target as Node)) {
        setConfigDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [configDropdownOpen, runTour, configDropdownRef, setConfigDropdownOpen]);

  const { size } = useWidth();
  const isMobile = size === 's';

  if (calendarEditMode || isSchedulingMode || isRescheduleMode) return null;

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end", width: "100%" }}>
      {!isRescheduleMode && (
        <button
          type="button"
          aria-label="schedule-session"
          id="btn-agendar-sesion"
          onClick={onScheduleSession}
          style={{
            display: "inline-flex",
            alignItems: "center",
            border: "2px solid #6D57BC",
            borderRadius: "9999px",
            padding: "0.5rem 0.875rem",
            background: "transparent",
            color: "#6D57BC",
            fontWeight: 500,
            fontSize: isMobile ? "0.875rem" : "0.8125rem",
            height: isMobile ? "40px" : "35px",
            cursor: "pointer",
            transition: "all 0.2s ease-out",
          }}
        >
          {isMobile ? "Agendar" : "Agendar sesión"}
        </button>
      )}

      <div ref={configDropdownRef} style={{ position: "relative", display: "inline-block" }}>
        <button
          type="button"
          aria-label="toggle-calendar-edit"
          id="btn-configurar-horarios"
          onClick={() => setConfigDropdownOpen(!configDropdownOpen)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.375rem",
            border: 0,
            borderRadius: "9999px",
            padding: "0.5rem 0.875rem",
            background: "#6D57BC",
            color: "#ffffff",
            fontWeight: 500,
            fontSize: isMobile ? "0.875rem" : "0.8125rem",
            height: isMobile ? "40px" : "35px",
            cursor: "pointer",
            transition: "all 0.2s ease-out",
          }}
          title="Configurar horarios"
        >
          <span>{isMobile ? "Horarios" : "Configurar horarios"}</span>
          <ChevronDownIcon
            style={{
              transition: "transform 0.2s",
              transform: configDropdownOpen ? "rotate(180deg)" : "rotate(0)",
            }}
          />
        </button>

        {configDropdownOpen && (
          <div
            id="config-dropdown-options"
            style={{
              position: "absolute",
              top: "calc(100% + 0.25rem)",
              right: 0,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "0.5rem",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              zIndex: 100,
              minWidth: "12.5rem",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <button
              type="button"
              onClick={() => {
                setConfigDropdownOpen(false);
                if (isTogglingEdit) return;
                onStartRecurrent();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.625rem",
                width: "100%",
                padding: "0.625rem 0.875rem",
                background: "transparent",
                border: 0,
                fontSize: "0.8125rem",
                color: "#374151",
                cursor: "pointer",
                textAlign: "left",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f0ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <CalendarRecurrentIcon />
              Horario fijo
            </button>

            <button
              type="button"
              onClick={() => {
                setConfigDropdownOpen(false);
                onStartSpecificDay();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.625rem",
                width: "100%",
                padding: "0.625rem 0.875rem",
                background: "transparent",
                border: 0,
                fontSize: "0.8125rem",
                color: "#374151",
                cursor: "pointer",
                textAlign: "left",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f0ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <CalendarDayIcon />
              Día específico
            </button>

            <button
              type="button"
              onClick={() => {
                setConfigDropdownOpen(false);
                onStartBlock();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.625rem",
                width: "100%",
                padding: "0.625rem 0.875rem",
                background: "transparent",
                border: 0,
                fontSize: "0.8125rem",
                color: "#374151",
                cursor: "pointer",
                textAlign: "left",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f0ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <BlockIcon />
              Bloquear espacios
            </button>

            {/* La app mobile no tiene la guía ni los avisos de ayuda de la Agenda de la web (tours y modales de ayuda). */}
            {!hideHelp && (
              <>
            {/* Divider */}
            <div style={{ height: "1px", background: "#f3f4f6", margin: "0.125rem 0" }} />

            {/* Help section label */}
            <div
              data-testid="help-section-label"
              className="px-3.5 pt-2 pb-0.5 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] pointer-events-none select-none"
              style={{ color: "#b3a4d0" }}
            >
              Ayuda
            </div>

            <button
              type="button"
              id="btn-que-significa"
              onClick={() => {
                setConfigDropdownOpen(false);
                onOpenHelp?.();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                width: "100%",
                padding: "0.5rem 0.875rem",
                background: "transparent",
                border: 0,
                fontSize: "0.75rem",
                fontWeight: 400,
                color: "#8475b3",
                cursor: "pointer",
                textAlign: "left",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f0ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <InfoCircleIcon size={14} />
              Cómo funciona tu agenda
            </button>

            <button
              type="button"
              id="btn-reschedule-info"
              data-testid="reschedule-info-btn"
              onClick={() => {
                setConfigDropdownOpen(false);
                onOpenRescheduleInfo?.();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                width: "100%",
                padding: "0.5rem 0.875rem",
                background: "transparent",
                border: 0,
                fontSize: "0.75rem",
                fontWeight: 400,
                color: "#8475b3",
                cursor: "pointer",
                textAlign: "left",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f0ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <InfoCircleIcon size={14} />
              Cómo funcionan los horarios de reprogramación
            </button>

            <button
              type="button"
              id="btn-repetir-guia"
              aria-label="replay-calendar-onboarding"
              onClick={() => {
                setConfigDropdownOpen(false);
                onReplayTour();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                width: "100%",
                padding: "0.5rem 0.875rem",
                background: "transparent",
                border: 0,
                fontSize: "0.75rem",
                fontWeight: 400,
                color: "#8475b3",
                cursor: "pointer",
                textAlign: "left",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f0ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <InfoCircleIcon size={14} />
              Repasar guía de Agenda
            </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
